// File summary (cover, title, key numbers), composition bar, observations, tags and format primer.
import { store } from '../state';
import { composition, type Model } from '../model';
import { channelName, fmtBytes, fmtHz } from '../parsers/bits';
import { KIND_HELP, KIND_LABEL, KIND_ORDER } from '../content/kinds';
import { FORMATS } from '../content/formats';
import { h, richText, tip } from './dom';
import { theme } from './theme';
import { fmtTime, reveal } from './anatomy';
import { LANE_COLORS, type CodecCache } from './codec';

const STAT_HELP: Record<string, string> = {
  Codec: 'The method used to store the sound. Lossy codecs (MP3, AAC, Vorbis, Opus) remove detail you are unlikely to hear. Lossless codecs (FLAC, ALAC, PCM) keep every bit.',
  Container: 'The file layout that wraps the audio: how blocks, tags and timing are arranged.',
  'Sample rate': 'How many times per second the sound wave is measured. 44,100 Hz (CD) can hold frequencies up to 22,050 Hz, just above human hearing.',
  Channels: 'Separate audio signals. Stereo has two: left and right.',
  'Bit depth': 'How precisely each measurement is stored. 16-bit gives 65,536 levels, about 96 dB of dynamic range.',
  Duration: 'How long the sound plays.',
  Bitrate: 'How many bits one second of sound uses on average. Higher usually means better quality and a bigger file. CBR = constant, VBR = variable.',
  Size: 'The total size of the file on disk.',
  Encoder: 'The software that made this file.',
  Delay: 'Silent samples the encoder added at the start. Gapless players remove them.',
};

export function summaryCard(m: Model): HTMLElement {
  const pf = m.pf, a = pf.audio;
  const title = pf.tags.find((t) => t.key === 'Title')?.value || m.name;
  const artist = [pf.tags.find((t) => t.key === 'Artist')?.value, pf.tags.find((t) => t.key === 'Album')?.value].filter(Boolean).join(' — ');

  let cover: HTMLElement;
  const pic = pf.pictures[0];
  if (pic && pic.end > pic.start) {
    const blob = new Blob([m.bytes.slice(pic.start, pic.end)], { type: pic.mime || 'image/jpeg' });
    const img = h('img', { class: 'cover', alt: 'Cover art', src: URL.createObjectURL(blob), title: 'Embedded cover art — click to see where it is stored' });
    img.onclick = () => {
      const seg = findImageSeg(m, pic.start);
      if (seg) { store.set({ sel: { kind: 'seg', seg } }); reveal({ kind: 'seg', seg }); }
    };
    img.onerror = () => img.replaceWith(h('div', { class: 'cover empty' }, 'no preview'));
    cover = img;
  } else cover = h('div', { class: 'cover empty' }, 'No cover art');

  const stats: [string, string][] = [
    ['Codec', a.codec + (a.lossless ? ' (lossless)' : '')],
    ['Container', pf.container],
    ['Sample rate', a.sampleRate ? fmtHz(a.sampleRate) : '—'],
    ['Channels', a.channels ? `${a.channels} · ${a.channelMode ?? channelName(a.channels)}` : '—'],
  ];
  if (a.bitsPerSample) stats.push(['Bit depth', `${a.bitsPerSample}-bit`]);
  stats.push(['Duration', m.duration ? fmtTime(m.duration, 30) : '—']);
  stats.push(['Bitrate', a.bitrate ? `${Math.round(a.bitrate / 1000).toLocaleString('en-US')} kbps${a.bitrateMode && !['Lossless', 'Uncompressed'].includes(a.bitrateMode) ? ' ' + a.bitrateMode : ''}` : '—']);
  stats.push(['Size', fmtBytes(pf.size)]);
  if (a.encoder) stats.push(['Encoder', a.encoder]);
  if (a.encoderDelay) stats.push(['Delay', `${a.encoderDelay.toLocaleString('en-US')} samples`]);

  const grid = h('div', { class: 'stats' });
  for (const [k, v] of stats) {
    const dot = h('span', { class: 'info-dot' }, 'i');
    if (STAT_HELP[k]) tip(dot, STAT_HELP[k]);
    const vEl = h('div', { class: 'v', title: v }, v);
    grid.append(h('div', { class: 'stat' }, h('div', { class: 'k' }, k, STAT_HELP[k] ? dot : null), vEl));
  }

  return h('section', { class: 'card summary' }, cover,
    h('div', { style: { minWidth: '0' } },
      h('h2', { class: 'sum-title' }, title),
      h('div', { class: 'sum-artist' }, artist || (a.codecLong ?? '')),
      grid));
}

function findImageSeg(m: Model, start: number) {
  const path = m.segmentAt(start, 1e9, 0);
  return [...path].reverse().find((s) => s.kind === 'image') ?? path[path.length - 1];
}

export function compositionCard(m: Model): HTMLElement {
  const T = theme();
  const comp = composition(m);
  const total = m.pf.size || 1;
  const bar = h('div', { class: 'comp-bar', role: 'img', 'aria-label': 'Share of the file by block type' });
  const legend = h('div', { class: 'legend' });
  for (const k of KIND_ORDER) {
    const n = comp.get(k) ?? 0;
    if (!n) continue;
    const p = (n / total) * 100;
    const seg = h('span', { style: { width: `${p}%`, background: T.kind[k] } });
    tip(seg, `<b>${KIND_LABEL[k]}</b>: ${fmtBytes(n)} (${p < 0.1 ? '<0.1' : p.toFixed(1)}%)<br>${KIND_HELP[k]}`);
    bar.append(seg);
    const it = h('span', { class: 'it' }, h('span', { class: 'sw', style: { background: T.kind[k] } }), KIND_LABEL[k], h('span', { class: 'pct' }, p < 0.1 ? '<0.1%' : `${p.toFixed(p < 10 ? 1 : 0)}%`), h('span', { class: 'dim' }, fmtBytes(n)));
    tip(it, KIND_HELP[k]);
    legend.append(it);
  }
  return h('section', { class: 'card comp' },
    h('div', { style: { display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' } },
      h('b', {}, 'What this file is made of'), h('span', { class: 'dim' }, `${m.pf.size.toLocaleString('en-US')} bytes`)),
    bar, legend);
}

export function bottomCards(m: Model): HTMLElement {
  const pf = m.pf;
  const primer = FORMATS[pf.format];
  const left = h('div', { style: { display: 'flex', flexDirection: 'column', gap: '16px' } });
  const right = h('div', { style: { display: 'flex', flexDirection: 'column', gap: '16px' } });

  if (pf.notes.length) {
    const ul = h('ul', { class: 'notes' });
    for (const n of pf.notes) ul.append(h('li', { class: n.level === 'warn' ? 'warn' : '', html: richText(n.text) }));
    left.append(h('section', { class: 'card' }, h('div', { class: 'card-h' }, 'What we noticed'), ul));
  }
  if (pf.tags.length) {
    const g = h('div', { class: 'tags' });
    for (const t of pf.tags.slice(0, 60)) {
      const k = h('span', { class: 'k', title: `Stored as ${t.rawKey}` }, t.key);
      g.append(k, h('span', { class: 'v' }, t.value.length > 400 ? t.value.slice(0, 400) + '…' : t.value));
    }
    left.append(h('section', { class: 'card' }, h('div', { class: 'card-h' }, 'Tags', h('span', { class: 'sub' }, `${pf.tags.length}`)), g));
  }
  if (primer) {
    const ol = h('ol', {});
    for (const l of primer.layout) ol.append(h('li', { html: richText(l) }));
    right.append(h('section', { class: 'card' },
      h('div', { class: 'card-h' }, `About ${primer.name}`, h('span', { class: 'sub' }, `since ${primer.year}`)),
      h('div', { class: 'primer' }, h('p', { html: richText(primer.about) }), h('div', { class: 'sec-h' }, 'Typical layout'), ol)));
  }
  right.append(h('section', { class: 'card' }, h('div', { class: 'card-h' }, 'Keyboard'),
    h('div', { class: 'keys' },
      h('kbd', {}, 'Space'), h('span', {}, 'Play or pause the selection'),
      h('kbd', {}, '← →'), h('span', {}, 'Step to the previous or next block (and hear it)'),
      h('kbd', {}, '↑'), h('span', {}, 'Select the parent block'),
      h('kbd', {}, '+ −'), h('span', {}, 'Zoom the sound in or out'),
      h('kbd', {}, 'Z'), h('span', {}, 'Zoom to the selection'),
      h('kbd', {}, 'F'), h('span', {}, 'Show the whole file'),
      h('kbd', {}, 'S'), h('span', {}, 'Switch waveform / spectrogram'),
      h('kbd', {}, 'L'), h('span', {}, 'Loop on or off'),
      h('kbd', {}, 'A'), h('span', {}, 'Play blocks as you step (on or off)'),
      h('kbd', {}, 'Esc'), h('span', {}, 'Clear the selection'))));
  return h('div', { class: 'bottom' }, left, right);
}

/** "What the encoder did": decision legends with their share of the file, plus codec-level notes. */
export function encoderCard(cc: CodecCache): HTMLElement {
  const body = h('div', { class: 'lanes-legend' });
  const notes = h('ul', { class: 'notes', style: { padding: '0 16px 8px' } });
  const status = h('span', { class: 'sub' }, 'analysing…');
  const card = h('section', { class: 'card' }, h('div', { class: 'card-h' }, 'What the encoder did', status), body, notes);
  const paint = () => {
    const F = cc.m.frames;
    const blocks = cc.c.lanes.map((ln, k) => {
      // Share of playing time for each value.
      const dur = new Float64Array(ln.values.length);
      let total = 0;
      for (let i = 0; i < cc.decided; i++) {
        const v = cc.lanes[k][i];
        const d = F[i].time![1] - F[i].time![0];
        if (v >= 0) { dur[v] += d; total += d; }
      }
      const vals = h('div', { class: 'll-vals' });
      ln.values.forEach((val, vi) => {
        const pct = total ? (dur[vi] / total) * 100 : 0;
        const it = h('span', {}, h('span', { class: 'sw', style: { background: LANE_COLORS[vi % LANE_COLORS.length] } }), val.value,
          h('span', { class: 'pct' }, total ? (pct > 0 && pct < 0.5 ? '<1%' : `${Math.round(pct)}%`) : ''));
        tip(it, `<b>${richText(val.value)}</b><br>${richText(val.help)}`);
        vals.append(it);
      });
      return h('div', {}, h('div', { class: 'll-name' }, ln.name), h('div', { class: 'll-help', html: richText(ln.help) }), vals);
    });
    body.replaceChildren(...blocks);
    status.textContent = cc.decisionsDone ? `${cc.c.codec} · ${F.length.toLocaleString('en-US')} frames` : `analysing… ${Math.round((cc.decided / Math.max(1, F.length)) * 100)}%`;
  };
  let pending = 0;
  let summarized = false;
  cc.on(() => {
    if (!pending) pending = window.setTimeout(() => {
      pending = 0;
      paint();
      if (cc.decisionsDone && !summarized) {
        summarized = true;
        setTimeout(() => {
          try {
            for (const n of cc.c.summarize?.() ?? []) notes.append(h('li', { class: n.level === 'warn' ? 'warn' : '', html: richText(n.text) }));
          } catch (e) {
            console.warn('summarize failed', e);
          }
        }, 50);
      }
    }, 250);
  });
  paint();
  return card;
}
